import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { forwardRef, memo, MouseEvent, useImperativeHandle, useMemo, useState } from 'react';

import * as api from '../../services/api';
import type {
  Criteria,
  CriteriaPurpose,
  CriterionTranscriptionAssignment,
  Essay,
  EssayPurpose,
  Purpose,
  Transcription,
  User,
} from '../../types';
import SearchableSelect from '../common/SearchableSelect';
import AssignmentHistoryPanel from './AssignmentHistoryPanel';
import {
  CANDIDATES_PER_PAGE,
  buildAssignmentCandidate,
  listAssignmentCandidates,
  selectableCandidateIds,
  sourceTranscriptionIds,
  type TranscriptionSource,
  userDisplayName,
} from './assignmentCandidates';

export interface AssignmentCreatePrefill {
  criteriaIds?: string[];
  transcriptionIds?: string[];
}

export interface AssignmentCreatePanelHandle {
  open: (prefill?: AssignmentCreatePrefill) => void;
}

interface Props {
  users: User[];
  criteria: Criteria[];
  transcriptions: Transcription[];
  essays: Essay[];
  essayPurposes: EssayPurpose[];
  purposes: Purpose[];
  criteriaPurposes: CriteriaPurpose[];
  assignments: CriterionTranscriptionAssignment[];
  initialCriteriaIds?: string[];
  onCreated: (created: CriterionTranscriptionAssignment[]) => void;
}

const CandidateRow = memo(
  ({
    candidate,
    selected,
    disabledReason,
    onToggle,
  }: {
    candidate: ReturnType<typeof listAssignmentCandidates>[number];
    selected: boolean;
    disabledReason: string | null;
    onToggle: (id: string) => void;
  }) => (
    <Paper variant="outlined" sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <Tooltip title={disabledReason ?? ''}>
        <span>
          <Checkbox
            size="small"
            checked={selected}
            disabled={Boolean(disabledReason) && !selected}
            onChange={() => onToggle(candidate.transcriptionId)}
            inputProps={{ 'aria-label': `Select paper ${candidate.paperNumber}` }}
          />
        </span>
      </Tooltip>
      <Box>
        <Typography variant="subtitle2">
          Paper {candidate.paperNumber} · {candidate.batchName || 'No batch'} ·{' '}
          {candidate.versionName}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {candidate.isGroundTruth && <Chip color="primary" size="small" label="Ground truth" />}
          {candidate.purposeMismatch && (
            <Chip color="error" size="small" label="Purpose mismatch" />
          )}
          {candidate.purposeNames.map(name => (
            <Chip key={name} size="small" label={name} />
          ))}
          {candidate.assignedUserNames.map(name => (
            <Chip key={name} size="small" variant="outlined" label={`Assigned to ${name}`} />
          ))}
        </Box>
      </Box>
    </Paper>
  )
);
CandidateRow.displayName = 'CandidateRow';

const AssignmentCreatePanel = forwardRef<AssignmentCreatePanelHandle, Props>(
  (
    {
      users,
      criteria,
      transcriptions,
      essays,
      essayPurposes,
      purposes,
      criteriaPurposes,
      assignments,
      initialCriteriaIds = [],
      onCreated,
    },
    ref
  ) => {
    const [userIds, setUserIds] = useState<string[]>([]);
    const [criteriaIds, setCriteriaIds] = useState<string[]>([]);
    const [showArchivedCriteria, setShowArchivedCriteria] = useState(false);
    const [purposeIds, setPurposeIds] = useState<string[]>([]);
    const [preferGroundTruth, setPreferGroundTruth] = useState(true);
    const [source, setSource] = useState<TranscriptionSource>('browse');
    const [referenceUserId, setReferenceUserId] = useState('');
    const [sourceCriterionId, setSourceCriterionId] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [candidatePage, setCandidatePage] = useState(1);
    const [assigning, setAssigning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [open, setOpen] = useState(false);

    const sortedUsers = useMemo(
      () =>
        [...users].sort((a, b) =>
          `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
        ),
      [users]
    );
    const sortedCriteria = useMemo(
      () => [...criteria].sort((a, b) => a.name.localeCompare(b.name)),
      [criteria]
    );
    const visibleCriteria = useMemo(
      () =>
        sortedCriteria.filter(
          c => showArchivedCriteria || !c.isArchived || criteriaIds.includes(c.id)
        ),
      [sortedCriteria, showArchivedCriteria, criteriaIds]
    );

    const availablePurposes = useMemo(() => {
      if (criteriaIds.length === 0) return purposes;
      const allowed = new Set(
        criteriaPurposes
          .filter(row => criteriaIds.includes(row.criteriaId))
          .map(row => row.purposeId)
      );
      return purposes.filter(purpose => allowed.has(purpose.id));
    }, [criteriaIds, criteriaPurposes, purposes]);

    const lookups = useMemo(
      () => ({
        transcriptions,
        essays,
        essayPurposes,
        purposes,
        criteriaPurposes,
        assignments,
        users,
      }),
      [assignments, criteriaPurposes, essayPurposes, essays, purposes, transcriptions, users]
    );

    const candidates = useMemo(
      () =>
        listAssignmentCandidates(lookups, {
          source,
          targetCriteriaIds: criteriaIds,
          purposeIds,
          preferGroundTruth,
          referenceUserId,
          sourceCriterionId,
        }),
      [
        criteriaIds,
        lookups,
        preferGroundTruth,
        purposeIds,
        referenceUserId,
        source,
        sourceCriterionId,
      ]
    );

    const visibleCandidates = useMemo(() => {
      const selectedSet = new Set(selectedIds);
      const byId = new Map(candidates.map(candidate => [candidate.transcriptionId, candidate]));
      const extras = selectedIds
        .filter(id => !byId.has(id))
        .map(id => {
          const transcription = transcriptions.find(item => item.id === id);
          if (!transcription) return null;
          return buildAssignmentCandidate(transcription, lookups, criteriaIds, criteriaIds);
        })
        .filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate));
      return [...extras, ...candidates].sort((left, right) => {
        const leftSelected = selectedSet.has(left.transcriptionId) ? 1 : 0;
        const rightSelected = selectedSet.has(right.transcriptionId) ? 1 : 0;
        return rightSelected - leftSelected;
      });
    }, [candidates, criteriaIds, lookups, selectedIds, transcriptions]);

    const selectableIds = useMemo(
      () => selectableCandidateIds(visibleCandidates, criteriaIds, userIds, assignments),
      [assignments, criteriaIds, userIds, visibleCandidates]
    );
    const selectableIdSet = useMemo(() => new Set(selectableIds), [selectableIds]);

    const candidatePageCount = Math.max(
      1,
      Math.ceil(visibleCandidates.length / CANDIDATES_PER_PAGE)
    );
    const safePage = Math.min(candidatePage, candidatePageCount);
    const pagedCandidates = visibleCandidates.slice(
      (safePage - 1) * CANDIDATES_PER_PAGE,
      safePage * CANDIDATES_PER_PAGE
    );

    const getTranscriptionDisplayLabel = (transcription: Transcription): string => {
      const essay = essays.find(item => item.id === transcription.essayId);
      if (!essay) return transcription.versionName;
      const names = essayPurposes
        .filter(row => row.essayId === essay.id)
        .map(row => purposes.find(purpose => purpose.id === row.purposeId)?.name)
        .filter((name): name is string => Boolean(name))
        .join(', ');
      return `${names || 'N/A'} - ${essay.learn_cycle_batch} - ${essay.paper_number} - ${transcription.versionName}`;
    };

    const assignedCriteriaForReference = useMemo(() => {
      if (!referenceUserId) return [];
      const assignedIds = new Set(
        assignments
          .filter(assignment => assignment.userId === referenceUserId)
          .map(a => a.criteriaId)
      );
      return sortedCriteria.filter(criterion => assignedIds.has(criterion.id));
    }, [assignments, referenceUserId, sortedCriteria]);

    const toggleCandidate = (id: string) => {
      setSelectedIds(current => {
        if (current.includes(id)) return current.filter(item => item !== id);
        if (!selectableIdSet.has(id)) return current;
        return [...current, id];
      });
    };

    const applyOpen = (prefill?: AssignmentCreatePrefill) => {
      setCriteriaIds(prefill?.criteriaIds ?? initialCriteriaIds);
      setSelectedIds(prefill?.transcriptionIds ?? []);
      setCandidatePage(1);
      setPurposeIds([]);
      setSource('browse');
      setReferenceUserId('');
      setSourceCriterionId('');
      setError(null);
      setOpen(true);
    };

    useImperativeHandle(ref, () => ({ open: applyOpen }));

    const handleSourceChange = (
      _event: MouseEvent<HTMLElement>,
      next: TranscriptionSource | null
    ) => {
      if (!next) return;
      setSource(next);
      setSelectedIds([]);
      setCandidatePage(1);
      if (next === 'browse') {
        setReferenceUserId('');
        setSourceCriterionId('');
      }
    };

    const handleSelectAllMatching = () => {
      setSelectedIds(selectableIds);
    };

    const handleOpen = () => {
      applyOpen();
    };

    const disabledReasonFor = (
      transcriptionId: string,
      purposeMismatch: boolean
    ): string | null => {
      if (userIds.length === 0) return 'Choose assignees first';
      if (criteriaIds.length === 0) return 'Choose Criteria first';
      if (purposeMismatch) return 'Purpose mismatch with selected Criteria';
      if (!selectableIdSet.has(transcriptionId)) {
        return 'Already assigned to every selected person for every selected Criterion';
      }
      return null;
    };

    const assign = async () => {
      if (userIds.length === 0 || criteriaIds.length === 0 || selectedIds.length === 0) return;
      setAssigning(true);
      setError(null);
      try {
        const results = await Promise.all(
          criteriaIds.map(criteriaId => api.bulkCreateAssignments(criteriaId, userIds, selectedIds))
        );
        const created = results.flatMap(result => result.assignments);
        const createdCount = results.reduce((sum, result) => sum + result.createdCount, 0);
        onCreated(created);
        setSelectedIds([]);
        setSuccess(
          createdCount === 1
            ? 'Created 1 assignment.'
            : `Created ${createdCount} assignments across ${userIds.length} people.`
        );
      } catch {
        setError('The selected transcriptions could not be assigned.');
      } finally {
        setAssigning(false);
      }
    };

    const emptyMessage = (() => {
      if (source === 'existing') {
        if (!referenceUserId || !sourceCriterionId) {
          return 'Choose a reference labeller and criterion to see their assigned transcriptions.';
        }
        if (visibleCandidates.length === 0) {
          return 'That labeller has no assignments for the selected criterion.';
        }
        return null;
      }
      if (criteriaIds.length === 0) return 'Choose Criteria to browse matching transcriptions.';
      if (visibleCandidates.length === 0) return 'No transcriptions match these filters.';
      return null;
    })();

    return (
      <>
        <Button variant="contained" onClick={handleOpen}>
          Assign labelling
        </Button>
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
          <DialogTitle>Assign labelling</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose people and Criteria, then pick transcriptions from the list. Copy from an
              existing assignment to reuse that labeller&apos;s set.
            </Typography>

            <Box
              sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}
            >
              <FormControl size="small" fullWidth>
                <InputLabel id="assign-to-label">Assign to</InputLabel>
                <SearchableSelect
                  labelId="assign-to-label"
                  multiple
                  value={userIds}
                  label="Assign to"
                  onChange={event =>
                    setUserIds(
                      typeof event.target.value === 'string'
                        ? event.target.value.split(',')
                        : event.target.value
                    )
                  }
                  options={sortedUsers.map(user => ({
                    value: user.id,
                    label: `${user.firstName} ${user.lastName} (${user.userType})`,
                  }))}
                  renderValue={selected => `${(selected as string[]).length} selected`}
                />
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="assign-criteria-label">Criteria</InputLabel>
                <SearchableSelect
                  labelId="assign-criteria-label"
                  multiple
                  value={criteriaIds}
                  label="Criteria"
                  onChange={event => {
                    const next =
                      typeof event.target.value === 'string'
                        ? event.target.value.split(',')
                        : event.target.value;
                    setCriteriaIds(next);
                    setSelectedIds([]);
                    setCandidatePage(1);
                  }}
                  options={visibleCriteria.map(criterion => ({
                    value: criterion.id,
                    label: criterion.isArchived
                      ? `${criterion.name} [${criterion.granularity}] (Archived)`
                      : `${criterion.name} [${criterion.granularity}]`,
                  }))}
                  renderValue={selected => `${(selected as string[]).length} selected`}
                />
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showArchivedCriteria}
                    onChange={event => setShowArchivedCriteria(event.target.checked)}
                    size="small"
                  />
                }
                label="Show archived criteria"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={preferGroundTruth}
                    onChange={event => setPreferGroundTruth(event.target.checked)}
                  />
                }
                label="Prefer ground-truth transcriptions"
              />
              <Box>
                <FormControl size="small" fullWidth>
                  <InputLabel id="candidate-purpose-label">Writing purposes</InputLabel>
                  <Select
                    labelId="candidate-purpose-label"
                    multiple
                    value={purposeIds}
                    label="Writing purposes"
                    onChange={event => {
                      setPurposeIds(event.target.value as string[]);
                      setCandidatePage(1);
                    }}
                    renderValue={selected =>
                      selected.length === 0 ? 'All purposes' : `${selected.length} selected`
                    }
                  >
                    {availablePurposes.map(purpose => (
                      <MenuItem key={purpose.id} value={purpose.id}>
                        <Checkbox checked={purposeIds.includes(purpose.id)} />
                        {purpose.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  disabled={purposeIds.length === 0}
                  onClick={() => setPurposeIds([])}
                >
                  Deselect all purposes
                </Button>
              </Box>
            </Box>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={source}
              onChange={handleSourceChange}
              sx={{ mt: 2 }}
              aria-label="Transcription source"
            >
              <ToggleButton value="browse">Browse matching transcriptions</ToggleButton>
              <ToggleButton value="existing">Copy from existing assignments</ToggleButton>
            </ToggleButtonGroup>

            {source === 'existing' && (
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  mt: 2,
                }}
              >
                <FormControl size="small" fullWidth>
                  <InputLabel id="reference-labeller-label">Reference labeller</InputLabel>
                  <SearchableSelect
                    labelId="reference-labeller-label"
                    value={referenceUserId}
                    label="Reference labeller"
                    onChange={event => {
                      setReferenceUserId(event.target.value as string);
                      setSourceCriterionId('');
                      setSelectedIds([]);
                      setCandidatePage(1);
                    }}
                    options={sortedUsers.map(user => ({
                      value: user.id,
                      label: `${user.firstName} ${user.lastName} (${user.userType})`,
                    }))}
                  />
                </FormControl>
                <FormControl size="small" fullWidth disabled={!referenceUserId}>
                  <InputLabel id="reference-criterion-label">Reference criterion</InputLabel>
                  <SearchableSelect
                    labelId="reference-criterion-label"
                    value={sourceCriterionId}
                    label="Reference criterion"
                    onChange={event => {
                      setSourceCriterionId(event.target.value as string);
                      setSelectedIds([]);
                      setCandidatePage(1);
                    }}
                    options={assignedCriteriaForReference.map(criterion => ({
                      value: criterion.id,
                      label: `${criterion.name} (${sourceTranscriptionIds(assignments, referenceUserId, criterion.id).length} transcripts)`,
                    }))}
                  />
                </FormControl>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {emptyMessage ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                {emptyMessage}
              </Alert>
            ) : (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Showing {(safePage - 1) * CANDIDATES_PER_PAGE + 1}–
                  {Math.min(safePage * CANDIDATES_PER_PAGE, visibleCandidates.length)} of{' '}
                  {visibleCandidates.length} transcriptions
                  {source === 'existing'
                    ? ` already assigned to ${userDisplayName(users.find(user => user.id === referenceUserId))} for ${sortedCriteria.find(item => item.id === sourceCriterionId)?.name ?? 'this criterion'}`
                    : ' matching the selected Criteria'}
                </Typography>
                <Box sx={{ mt: 0.5, maxHeight: 360, overflowY: 'auto', display: 'grid', gap: 1 }}>
                  {pagedCandidates.map(candidate => (
                    <CandidateRow
                      key={candidate.transcriptionId}
                      candidate={candidate}
                      selected={selectedIds.includes(candidate.transcriptionId)}
                      disabledReason={disabledReasonFor(
                        candidate.transcriptionId,
                        candidate.purposeMismatch
                      )}
                      onToggle={toggleCandidate}
                    />
                  ))}
                </Box>
                {candidatePageCount > 1 && (
                  <Pagination
                    count={candidatePageCount}
                    page={safePage}
                    onChange={(_event, page) => setCandidatePage(page)}
                    size="small"
                    sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}
                  />
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                disabled={selectableIds.length === 0}
                onClick={handleSelectAllMatching}
              >
                {userIds.length === 0 || criteriaIds.length === 0
                  ? 'Choose assignees and Criteria first'
                  : `Select all matching (${selectableIds.length})`}
              </Button>
              <Button
                variant="contained"
                disabled={userIds.length === 0 || selectedIds.length === 0 || assigning}
                onClick={assign}
              >
                {assigning ? (
                  <CircularProgress size={18} />
                ) : (
                  `Assign ${selectedIds.length} transcriptions to ${userIds.length} people`
                )}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Each selected transcription is assigned to every selected person for every selected
                Criterion. Already-assigned pairs are skipped.
              </Typography>
            </Box>

            <AssignmentHistoryPanel
              criteriaIds={criteriaIds}
              criteria={criteria}
              transcriptions={transcriptions}
              selectedTranscriptionIds={selectedIds}
              getTranscriptionDisplayLabel={getTranscriptionDisplayLabel}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={success !== null}
          autoHideDuration={5000}
          onClose={() => setSuccess(null)}
          message={success}
        />
      </>
    );
  }
);
AssignmentCreatePanel.displayName = 'AssignmentCreatePanel';

export default AssignmentCreatePanel;
